import React from 'react';
import { useTranslation } from 'react-i18next';
import { ImpostorMode } from '../../types/tableGame';
import InfoButton from './InfoButton';

interface ImpostorModeSelectorProps {
  impostorMode: ImpostorMode;
  impostorCount: number;
  playerCount: number;
  onModeChange: (mode: ImpostorMode) => void;
  onCountChange: (count: number) => void;
  disabled?: boolean;
}

const ImpostorModeSelector: React.FC<ImpostorModeSelectorProps> = ({
  impostorMode,
  impostorCount,
  playerCount,
  onModeChange,
  onCountChange,
  disabled = false
}) => {
  const { t } = useTranslation();
  // Calculate max impostors for normal mode (less than half)
  const maxNormalImpostors = Math.floor((playerCount - 1) / 2);

  // Validate impostor count when mode or player count changes
  React.useEffect(() => {
    if (impostorMode === 'normal' && impostorCount > maxNormalImpostors) {
      onCountChange(Math.max(1, maxNormalImpostors));
    }
  }, [impostorMode, playerCount, maxNormalImpostors, impostorCount, onCountChange]);

  return (
    <div className="mb-6">
      <div className="flex items-center mb-3">
        <label className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          {t('tableSetup.impostors.label')}
        </label>
        <InfoButton
          title={t('tableSetup.impostors.infoTitle')}
          content={t('tableSetup.impostors.infoContent')}
        />
      </div>

      {/* Mode Selection */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          type="button"
          onClick={() => onModeChange('normal')}
          disabled={disabled}
          className={`
            py-3 px-4 rounded-lg font-medium transition-all duration-200
            ${impostorMode === 'normal'
              ? 'bg-blue-600 text-white shadow-lg scale-105'
              : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 border-2 border-gray-200 dark:border-gray-800'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          {t('tableSetup.impostors.normal')}
        </button>

        <button
          type="button"
          onClick={() => onModeChange('free')}
          disabled={disabled}
          className={`
            py-3 px-4 rounded-lg font-medium transition-all duration-200
            ${impostorMode === 'free'
              ? 'bg-blue-600 text-white shadow-lg scale-105'
              : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 border-2 border-gray-200 dark:border-gray-800'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          {t('tableSetup.impostors.free')}
        </button>
      </div>

      {/* Impostor Count Input */}
      <div>
        <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
          {t('tableSetup.impostors.countLabel', { count: impostorCount })}
          {impostorMode === 'normal' && (
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
              {t('tableSetup.impostors.maxLabel', { max: maxNormalImpostors })}
            </span>
          )}
        </label>
        
        <input
          type="range"
          min={impostorMode === 'normal' ? 1 : 0}
          max={impostorMode === 'normal' ? maxNormalImpostors : playerCount}
          value={impostorCount}
          onChange={(e) => onCountChange(parseInt(e.target.value))}
          disabled={disabled}
          className={`
            w-full h-2 bg-gray-200 dark:bg-gray-800 rounded-lg appearance-none cursor-pointer
            accent-blue-600
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        />
        
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>{impostorMode === 'normal' ? '1' : '0'}</span>
          <span>{impostorMode === 'normal' ? maxNormalImpostors : playerCount}</span>
        </div>
      </div>
    </div>
  );
};

export default ImpostorModeSelector;
